import { getGetFrontRichiesteIdRiepilogoDaFirmareDownloadQueryOptions } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { getGetFrontRichiesteIdRiepilogoDownloadQueryOptions } from '@framework/api-client/api/inquiries-compile/riepilogo-richiesta/riepilogo-richiesta.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { useHasDocumentsInlineViewer } from '@framework/common-web/ui/documents/useHasDocumentsInlineViewer.tsx';
import { useAuthDownload } from '@framework/common-web/utils/download/useAuthDownload.ts';
import { Button, message, Tooltip } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { useTheme } from 'styled-components';
import { useButtonResponsiveProps } from './useButtonResponsiveProps.ts';

export interface IDownloadSummaryButtonProps {
  submissionId: string;
  /** Se true, scarica il riepilogo firmato; altrimenti quello non firmato. */
  signed?: boolean;
  disabled?: boolean;
  ignoreMobile?: boolean;
  forceIcon?: boolean;
  inline?: boolean;
  /** Sovrascrive il testo del pulsante */
  label?: string;
}

/**
 * Pulsante per scaricare il PDF di riepilogo della richiesta.
 * Supporta sia il riepilogo firmato che quello non firmato (da firmare).
 */
export function DownloadSummaryButton(props: IDownloadSummaryButtonProps) {
  const {
    submissionId,
    signed,
    disabled,
    forceIcon,
    ignoreMobile,
    inline,
    label,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const hasInlineViewer = useHasDocumentsInlineViewer();

  const isMobile = useBreakpoint(down('md'));

  const signedText = hasInlineViewer
    ? t(
        'riepilogo-firmato.visualizza-pdf-firmato',
        'Visualizza Riepilogo Firmato',
      )
    : t('riepilogo-firmato.scarica-pdf-firmato', 'Scarica Riepilogo Firmato');
  const unsignedText = hasInlineViewer
    ? t('riepilogo-firmato.visualizza-pdf-riepilogo', 'Visualizza Riepilogo')
    : t('riepilogo-firmato.scarica-pdf-riepilogo', 'Scarica Riepilogo');
  const buttonText = label ?? (signed ? signedText : unsignedText);

  const buttonResponsiveProps = useButtonResponsiveProps({
    buttonText,
    forceIcon,
    hasInlineViewer,
    ignoreMobile,
    isMobile,
    inline,
  });

  const getQueryOptions = signed
    ? getGetFrontRichiesteIdRiepilogoDownloadQueryOptions
    : getGetFrontRichiesteIdRiepilogoDaFirmareDownloadQueryOptions;

  const { download, isFetching } = useAuthDownload(
    getQueryOptions,
    [submissionId],
    {
      onError: e => {
        console.error('[DownloadSummaryButton] Error: ', { error: e });
        const errorKey = signed
          ? 'riepilogo-firmato.errore-in-download'
          : 'riepilogo-firmato.errore-in-download-riepilogo';
        const errorFallback = signed
          ? 'Si è verificato un problema durante il download del riepilogo firmato'
          : 'Si è verificato un problema durante il download del riepilogo';
        message.error(getNetworkErrorMessage(e, t(errorKey, errorFallback)));
      },
      showInlineViewer: hasInlineViewer,
      openInNewWindow: theme.openModulePdfInNewWindow,
    },
  );

  const handleClick = useCallback(() => {
    if (submissionId && !disabled) {
      download();
    }
  }, [download, disabled, submissionId]);

  const tooltipKey = signed
    ? 'riepilogo-firmato.tooltip-disabled'
    : 'riepilogo-firmato.tooltip-riepilogo-disabled';
  const tooltipFallback = signed
    ? 'Il riepilogo firmato non è disponibile'
    : 'Il riepilogo non è disponibile';
  const tooltipTitle =
    disabled && submissionId ? t(tooltipKey, tooltipFallback) : undefined;

  return (
    <Tooltip title={tooltipTitle}>
      <Button
        {...buttonResponsiveProps}
        onClick={handleClick}
        disabled={!submissionId || disabled}
        loading={isFetching}
        block={!inline && (!isMobile || !!ignoreMobile)}
        size="large"
        type={inline ? 'link' : 'default'}
      />
    </Tooltip>
  );
}
